import {
  Box,
  Card,
  CardContent,
  Chip,
  LinearProgress,
  Stack,
  Typography,
  Alert,
  Button,
} from "@mui/material";
import {
  DataGrid,
  DataGridProps,
  GridColDef,
  GridRowsProp,
  GridToolbarQuickFilter,
} from "@mui/x-data-grid";
import { ReactNode } from "react";

/**
 * DataTableShell — shared layout for all list/table pages.
 *
 * Goals:
 * - ONE canonical table experience across Transactions, Invoices, Pending Review,
 *   Invoice Gaps, Exceptions. Uses MUI X DataGrid under the hood so every page
 *   inherits: column sort, column resize, pagination, keyboard nav, sticky
 *   header, row hover, built-in quick filter.
 * - Consistent page header with title + subtitle + count chip.
 * - Slot-based filter bar so each page plugs in its domain-specific filters
 *   without diverging on layout.
 * - Smart empty state with an actionable message + optional "Reset filters"
 *   button — replaces the random "No items found" screens each page invented.
 * - Loading state uses LinearProgress, never a blank flash.
 *
 * Explicitly NOT a full abstraction of every DataGrid prop — pages pass
 * `gridProps` through for advanced customization (column visibility, initial
 * state, row click handlers).
 */

export interface DataTableShellProps<Row extends { id?: string; _id?: string }> {
  /** Page title rendered as h4 */
  title: string;
  /** Small description under the title */
  subtitle?: string;
  /** Rows after all filters have been applied */
  rows: GridRowsProp<Row>;
  /** Column definitions for the DataGrid */
  columns: GridColDef<Row>[];
  /** Whether the underlying Convex query is still loading (data === undefined) */
  loading?: boolean;
  /**
   * Total rows BEFORE filters. Used for the "filtered N of M" chip and to
   * decide whether the empty state should offer "Reset filters" vs "No data".
   */
  totalBeforeFilters?: number;
  /**
   * Filter controls (Select, Slider, etc.) to render in the toolbar row.
   * Pages build their own filter bar and pass it here so the layout stays
   * consistent.
   */
  filterBar?: ReactNode;
  /**
   * Called when the user clicks "Reset filters" in the empty state. When
   * omitted, the button is not shown.
   */
  onResetFilters?: () => void;
  /** Singular label for the count chip, e.g. "transaction" → "N transactions" */
  itemLabel?: string;
  /** Optional alert rendered above the table (e.g. "showing first 500") */
  notice?: ReactNode;
  /** Message shown when rows is empty AND no filters are applied */
  emptyTitle?: string;
  /** Description for the no-data empty state */
  emptyDescription?: string;
  /** Icon for the no-data empty state */
  emptyIcon?: ReactNode;
  /** Extra DataGrid props passed through (sorting state, column visibility, row click, etc.) */
  gridProps?: Partial<Omit<DataGridProps<Row>, "rows" | "columns">>;
  /** Optional extra content rendered inside the card above the DataGrid (e.g. bulk action bar) */
  headerExtra?: ReactNode;
  /** data-tour attribute placed on the outer Box for guided-tour targeting */
  "data-tour"?: string;
}

export function DataTableShell<Row extends { id?: string; _id?: string }>({
  title,
  subtitle,
  rows,
  columns,
  loading = false,
  totalBeforeFilters,
  filterBar,
  onResetFilters,
  itemLabel = "item",
  notice,
  emptyTitle = "No data yet",
  emptyDescription,
  emptyIcon,
  gridProps,
  headerExtra,
  "data-tour": dataTour,
}: DataTableShellProps<Row>) {
  const rowCount = rows.length;
  const isFilteredDown =
    typeof totalBeforeFilters === "number" && totalBeforeFilters > rowCount;

  return (
    <Box data-tour={dataTour}>
      {/* Page header — title, subtitle, and the canonical "N items" chip */}
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="center"
        sx={{ mb: 3 }}
      >
        <Box>
          <Typography variant="h4">{title}</Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          )}
        </Box>
        {!loading && rowCount > 0 && (
          <Chip
            size="small"
            variant="outlined"
            label={
              isFilteredDown
                ? `${rowCount} of ${totalBeforeFilters} ${itemLabel}${rowCount !== 1 ? "s" : ""}`
                : `${rowCount} ${itemLabel}${rowCount !== 1 ? "s" : ""}`
            }
            sx={{ fontWeight: 600 }}
          />
        )}
      </Stack>

      {/* Filter bar slot */}
      {filterBar && (
        <Stack
          direction="row"
          spacing={2}
          sx={{ mb: 2 }}
          flexWrap="wrap"
          useFlexGap
          alignItems="flex-end"
          data-tour={dataTour ? `${dataTour}-filters` : undefined}
        >
          {filterBar}
        </Stack>
      )}

      {notice && <Box sx={{ mb: 2 }}>{notice}</Box>}

      {loading ? (
        <Card sx={{ border: "1px solid var(--c-overlay-6)" }}>
          <CardContent>
            <LinearProgress sx={{ height: 3, borderRadius: 2 }} />
            <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
              Loading...
            </Typography>
          </CardContent>
        </Card>
      ) : rowCount === 0 ? (
        <Card
          sx={{
            background:
              "linear-gradient(135deg, rgba(108,92,231,0.05), rgba(0,206,201,0.03))",
            border: "1px solid rgba(108,92,231,0.1)",
            textAlign: "center",
            py: 6,
          }}
        >
          <CardContent>
            {emptyIcon && (
              <Box
                sx={{
                  width: 64,
                  height: 64,
                  mx: "auto",
                  mb: 2,
                  borderRadius: 3,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(108,92,231,0.08)",
                  border: "1px solid rgba(108,92,231,0.18)",
                }}
              >
                {emptyIcon}
              </Box>
            )}
            <Typography variant="h6" sx={{ mb: 1 }}>
              {isFilteredDown ? "No matches for the current filters" : emptyTitle}
            </Typography>
            <Typography color="text.secondary" sx={{ mb: isFilteredDown && onResetFilters ? 2 : 0 }}>
              {isFilteredDown
                ? `Your filters excluded every row. ${
                    typeof totalBeforeFilters === "number"
                      ? `${totalBeforeFilters} ${itemLabel}${totalBeforeFilters !== 1 ? "s" : ""} exist in this scope.`
                      : ""
                  } Try a different cycle or reset the filters.`
                : emptyDescription}
            </Typography>
            {isFilteredDown && onResetFilters && (
              <Button
                size="small"
                variant="outlined"
                onClick={onResetFilters}
                sx={{ mt: 1 }}
              >
                Reset filters
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card
          sx={{
            border: "1px solid var(--c-overlay-6)",
            overflow: "hidden",
            "& .MuiDataGrid-root": {
              border: "none",
              "& .MuiDataGrid-cell": {
                borderColor: "var(--c-overlay-4)",
              },
              "& .MuiDataGrid-columnHeaders": {
                borderColor: "var(--c-overlay-6)",
              },
              "& .MuiDataGrid-footerContainer": {
                borderColor: "var(--c-overlay-6)",
              },
              "& .MuiDataGrid-row:hover": {
                backgroundColor: "rgba(108,92,231,0.04)",
              },
            },
          }}
        >
          {headerExtra}
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => (row.id ?? row._id) as string}
            initialState={{
              pagination: { paginationModel: { pageSize: 25 } },
              ...gridProps?.initialState,
            }}
            pageSizeOptions={[10, 25, 50, 100]}
            disableRowSelectionOnClick
            autoHeight
            sx={{ minHeight: 400 }}
            {...gridProps}
          />
        </Card>
      )}
    </Box>
  );
}

/**
 * Re-export the DataGrid's built-in quick filter so pages can optionally drop
 * it into their filter bar without importing from @mui/x-data-grid directly.
 */
export { GridToolbarQuickFilter };
